import React, { useState, useEffect } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Copy, ExternalLink, Play, Square, FileCode, RefreshCw } from "lucide-react"
import WithToast from "@/components/WithToast"
import { Toaster } from "@/components/ui/toaster"
import { AuthorizationModal } from "@/components/AuthorizationModal"
import { useApi } from "@/hooks/useApi"
import { createHookService, createAccountService, createUserManagementService } from "@/services/apiService"
import { PlanIds } from "../../../shared/client-server/types"
import { formatNumber } from "../../../shared/client-server/utils"

interface Hook {
    id: string
    event: string
    isEnabled: boolean
    shopUrl: string
    webhookId: string
    webhookSlug: string
    isPolling: boolean
    usage: number
    name?: string // Keep for backward compatibility
}

interface AccountDetailsProps {
    storeUrl: string
    showToastMessage: (message: string, title?: string) => void
}

// Define the types of operations that require authorization
type AuthOperation = {
    type: "toggle-hook" | "copy-code"
    hookId: string
    hookName: string
    currentStatus?: boolean
}

interface AccountData {
    id: string;
    shopUrl: string;
    email: string;
    plan: {
        id: PlanIds;
    };
    trialExpiresAt: string | null;
}

// Format trial expiry date as yyyy.mm.dd h:mm (hour not zero padded)
const formatTrialExpiryDate = (dateString: string): string => {
    const date = new Date(dateString);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    // Get hours without zero padding
    const hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}.${month}.${day} ${hours}:${minutes}`;
};

// Format amount in cents to dollars with $ sign
const formatAmount = (cents: number): string => {
    // Convert cents to dollars
    const dollars = cents / 100;
    // Format with 2 decimal places, dot as decimal separator, and comma as thousands separator
    return '$' + formatNumber(dollars, 2, '.', ',');
};

function AccountDetailsPane({ storeUrl, showToastMessage }: AccountDetailsProps) {
    const api = useApi();
    const hookService = createHookService(api);
    const accountService = createAccountService(api);
    const userManagementService = createUserManagementService(api);
    const [activeMenuItem, setActiveMenuItem] = useState("trial-accounts")
    const [isRefreshing, setIsRefreshing] = useState(false)
    const [isRevenueLoading, setIsRevenueLoading] = useState(true)
    const [isUsageLoading, setIsUsageLoading] = useState(true)
    const [isHooksLoading, setIsHooksLoading] = useState(true)
    const [isAccountLoading, setIsAccountLoading] = useState(true)
    const [revenue, setRevenue] = useState<{ amount: number; cap: number; lost: number } | null>(null)
    const [usage, setUsage] = useState<number | null>(null)
    const [hooks, setHooks] = useState<Hook[]>([])
    const [accountData, setAccountData] = useState<AccountData | null>(null)

    // Get current month in format mm-yyyy
    const getCurrentMonthSelector = () => {
        const now = new Date();
        const month = now.getMonth() + 1; // JavaScript months are 0-indexed
        const year = now.getFullYear();
        return `${month}-${year}`;
    }

    const [monthSelector, setMonthSelector] = useState(getCurrentMonthSelector())

    // State for authorization modal
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false)
    const [currentOperation, setCurrentOperation] = useState<AuthOperation | null>(null)

    // Unified function to fetch all data
    const fetchAllData = async (isRefreshAction = false) => {
        // Set loading states
        setIsRevenueLoading(true)
        setIsUsageLoading(true)
        setIsHooksLoading(true)
        setIsAccountLoading(true)

        if (isRefreshAction) {
            setIsRefreshing(true)
        }

        try {
            // Fetch hooks data
            const response = await hookService.getHooksByShopAndMonth(storeUrl, monthSelector);
            if (response.data.success) {
                setHooks(response.data.hooks);
            } else {
                showToastMessage('Failed to fetch hooks data.', 'Error');
            }
        } catch (err) {
            console.error('Error fetching hooks:', err);
            showToastMessage('Error fetching hooks data. Please try again later.', 'Error');
        } finally {
            setIsHooksLoading(false);
        }

        // Fetch revenue data
        try {
            const revenueResponse = await accountService.getRevenueByShopAndMonth(storeUrl, monthSelector);
            if (revenueResponse.data.success) {
                setRevenue(revenueResponse.data.revenue);
            } else {
                showToastMessage('Failed to fetch revenue data.', 'Error');
            }
        } catch (err) {
            console.error('Error fetching revenue data:', err);
            showToastMessage('Error fetching revenue data. Please try again later.', 'Error');
        } finally {
            setIsRevenueLoading(false);
        }

        // Fetch usage data
        try {
            const usageResponse = await accountService.getUsageByShopAndMonth(storeUrl, monthSelector);
            if (usageResponse.data.success) {
                setUsage(usageResponse.data.usage);
            } else {
                showToastMessage('Failed to fetch usage data.', 'Error');
            }
        } catch (err) {
            console.error('Error fetching usage data:', err);
            showToastMessage('Error fetching usage data. Please try again later.', 'Error');
        } finally {
            setIsUsageLoading(false);
        }

        // Fetch account data
        try {
            const accountResponse = await accountService.getAccountByShopUrl(storeUrl);
            if (accountResponse.data.success) {
                setAccountData(accountResponse.data.account);
            } else {
                showToastMessage('Failed to fetch account data.', 'Error');
            }
        } catch (err) {
            console.error('Error fetching account data:', err);
            showToastMessage('Error fetching account data. Please try again later.', 'Error');
        } finally {
            setIsAccountLoading(false);
        }

        if (isRefreshAction) {
            setIsRefreshing(false)
        }
    }

    // Fetch data from server when component mounts or when storeUrl/monthSelector changes
    useEffect(() => {
        fetchAllData()
    }, [storeUrl, monthSelector])

    // Handle refresh button click
    const handleRefresh = async () => {
        await fetchAllData(true)
    }

    const copyToClipboard = (text: string, label: string) => {
        navigator.clipboard.writeText(text)
        showToastMessage(`${label} copied to clipboard`, "Copied!")
    }

    // Server request for hook status toggle
    const toggleHookRequest = async (hookId: string, newStatus: boolean, authKey: string): Promise<void> => {
        // Call the appropriate API endpoint based on the new status
        // The authorization key is sent with the request and validated by the server
        if (newStatus) {
            await hookService.enableHook(hookId, authKey);
        } else {
            await hookService.disableHook(hookId, authKey);
        }
    }

    // Server request for code access
    const getHookCodeRequest = async (hookId: string, authKey: string): Promise<string> => {
        // Find the hook
        const hook = hooks.find((h) => h.id === hookId);
        if (!hook) {
            throw new Error("Hook not found");
        }

        // Make API call to get hook code with authorization key
        try {
            const response = await hookService.getHookCode(hookId, authKey);
            if (response.data.success) {
                return response.data.code;
            } else {
                throw new Error(response.data.message || "Failed to get hook code");
            }
        } catch (error) {
            console.error("Error getting hook code:", error);
            throw new Error("Failed to get hook code. Please try again later.");
        }
    }

    // Initiate toggle hook status flow
    const initiateToggleHookStatus = (hookId: string) => {
        const hook = hooks.find((h) => h.id === hookId)
        if (!hook) return

        setCurrentOperation({
            type: "toggle-hook",
            hookId,
            hookName: hook.name || hook.event,
            currentStatus: hook.isEnabled,
        })
        setIsAuthModalOpen(true)
    }

    // Initiate copy code flow
    const initiateCopyCode = (hookId: string) => {
        const hook = hooks.find((h) => h.id === hookId)
        if (!hook) return

        setCurrentOperation({
            type: "copy-code",
            hookId,
            hookName: hook.name || hook.event,
        })
        setIsAuthModalOpen(true)
    }

    // Handle authorization confirmation
    const handleAuthConfirm = async (authKey: string): Promise<void> => {
        if (!currentOperation) {
            throw new Error("No operation specified")
        }

        if (currentOperation.type === "toggle-hook") {
            const hook = hooks.find((h) => h.id === currentOperation.hookId)
            if (!hook) {
                throw new Error("Hook not found")
            }

            const newStatus = !hook.isEnabled

            // Call server to toggle hook status
            await toggleHookRequest(currentOperation.hookId, newStatus, authKey)

            // Update local state
            setHooks(
                hooks.map((h) => {
                    if (h.id === currentOperation.hookId) {
                        const actionMessage = h.isEnabled ? "Hook disabled" : "Hook enabled"
                        showToastMessage(actionMessage, "Status Changed")
                        return { ...h, isEnabled: !h.isEnabled }
                    }
                    return h
                }),
            )
        } else if (currentOperation.type === "copy-code") {
            // Get hook code from server
            const code = await getHookCodeRequest(currentOperation.hookId, authKey)

            // Copy to clipboard
            copyToClipboard(code, "Hook code")
        }
    }

    const copyHookData = (hook: Hook) => {
        // Create a new object with only the required properties
        const hookData = {
            id: hook.id,
            event: hook.event,
            isEnabled: hook.isEnabled,
            shopUrl: hook.shopUrl,
            webhookId: hook.webhookId,
            webhookSlug: hook.webhookSlug,
            isPolling: hook.isPolling,
            usage: hook.usage
        }
        const data = JSON.stringify(hookData, null, 2)
        copyToClipboard(data, "Hook data")
    }

    return (
        <>
            <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center space-x-2">
                    <a
                        href={`https://${storeUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline flex items-center"
                    >
                        {storeUrl}
                        <ExternalLink className="ml-1 h-4 w-4" />
                    </a>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => copyToClipboard(storeUrl, "Store URL")}
                    >
                        <Copy className="h-4 w-4" />
                    </Button>
                </div>

                <div className="flex items-center space-x-2 ml-4">
                    <span>{isAccountLoading ? "Loading..." : accountData?.email || "No email available"}</span>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => copyToClipboard(accountData?.email || "No email available", "Email address")}
                        disabled={isAccountLoading || !accountData?.email}
                    >
                        <Copy className="h-4 w-4" />
                    </Button>
                </div>

                <Button onClick={handleRefresh} disabled={isRefreshing} className="ml-auto">
                    {isRefreshing ? (
                        <>
                            <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                            Refreshing...
                        </>
                    ) : (
                        "Refresh"
                    )}
                </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Billing</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {isAccountLoading ? (
                            <>
                                <div className="h-10 w-24 bg-gray-200 animate-pulse rounded-md"></div>
                                <div className="h-5 w-48 bg-gray-200 animate-pulse rounded-md mt-2"></div>
                            </>
                        ) : (
                            <>
                                <div className="text-4xl font-bold">
                                    {accountData?.plan?.id === PlanIds.Trial && "Trial"}
                                    {accountData?.plan?.id === PlanIds.Free && "Free"}
                                    {accountData?.plan?.id === PlanIds.Pro && "Pro"}
                                    {accountData?.plan?.id === PlanIds.Custom && "Pro Bespoke"}
                                </div>
                                {accountData?.plan?.id === PlanIds.Trial && accountData?.trialExpiresAt && (
                                    <p className="text-sm text-muted-foreground mt-2">
                                        Ends on {formatTrialExpiryDate(accountData.trialExpiresAt)}
                                    </p>
                                )}
                            </>
                        )}
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Monthly revenue</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {isRevenueLoading ? (
                            <>
                                <div className="h-10 w-24 bg-gray-200 animate-pulse rounded-md"></div>
                                <div className="h-5 w-48 bg-gray-200 animate-pulse rounded-md mt-2"></div>
                            </>
                        ) : (
                            <>
                                <div className="text-4xl font-bold">{formatAmount(revenue?.amount || 0)}</div>
                                <p className="text-sm text-muted-foreground mt-2">
                                    Cap: {formatAmount(revenue?.cap || 0)}, Lost: {formatAmount(revenue?.lost || 0)}
                                </p>
                            </>
                        )}
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Usage this month</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {isUsageLoading ? (
                            <div className="h-10 w-24 bg-gray-200 animate-pulse rounded-md"></div>
                        ) : (
                            <div className="text-4xl font-bold">{usage}</div>
                        )}
                    </CardContent>
                </Card>
            </div>

            <div className="border rounded-md">
                {isHooksLoading ? (
                    <div className="p-4">
                        <div className="h-8 w-full bg-gray-200 animate-pulse rounded-md mb-4"></div>
                        <div className="h-12 w-full bg-gray-200 animate-pulse rounded-md mb-2"></div>
                        <div className="h-12 w-full bg-gray-200 animate-pulse rounded-md mb-2"></div>
                        <div className="h-12 w-full bg-gray-200 animate-pulse rounded-md"></div>
                    </div>
                ) : (
                    <Table>
                        <TableHeader className="bg-gray-100">
                            <TableRow>
                                <TableHead className="py-2">Hook name</TableHead>
                                <TableHead className="py-2">Id</TableHead>
                                <TableHead className="py-2">Trigger type</TableHead>
                                <TableHead className="py-2 text-right">Usage</TableHead>
                                <TableHead className="py-2">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {hooks.map((hook) => (
                                <TableRow key={hook.id}>
                                    <TableCell className="py-2">{hook.name || hook.event}</TableCell>
                                    <TableCell className="py-2 font-mono text-sm">{hook.id}</TableCell>
                                    <TableCell className="py-2">
                                        {hook.event}{hook.isPolling ? " / Polling" : ""}
                                    </TableCell>
                                    <TableCell className="py-2 text-right">{hook.usage.toLocaleString()}</TableCell>
                                    <TableCell className="py-2">
                                        <div className="flex justify-start space-x-2">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => initiateToggleHookStatus(hook.id)}
                                                className={hook.isEnabled ? "text-red-600 h-8 w-8 p-0" : "text-green-600 h-8 w-8 p-0"}
                                                title={hook.isEnabled ? "Disable hook" : "Enable hook"}
                                            >
                                                {hook.isEnabled ? <Square className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-8 w-8 p-0"
                                                onClick={() => initiateCopyCode(hook.id)}
                                                title="Copy hook code"
                                            >
                                                <FileCode className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-8 w-8 p-0"
                                                onClick={() => copyHookData(hook)}
                                                title="Copy hook data"
                                            >
                                                <Copy className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </div>

            {/* Authorization Modal */}
            <AuthorizationModal
                isOpen={isAuthModalOpen}
                onClose={() => setIsAuthModalOpen(false)}
                onConfirm={handleAuthConfirm}
                title={
                    currentOperation?.type === "toggle-hook"
                        ? `${currentOperation.currentStatus ? "Disable" : "Enable"} Hook`
                        : "Access Hook Code"
                }
                description={
                    currentOperation?.type === "toggle-hook"
                        ? `Authorization required to ${
                            currentOperation.currentStatus ? "disable" : "enable"
                        } the hook "${currentOperation.hookName}"`
                        : `Authorization required to view the code for hook "${currentOperation?.hookName}"`
                }
            />

            <Toaster />
        </>
    )
}

export default WithToast(AccountDetailsPane)
